/**
 * Releases on the wire (PLAN §2, §4, §11, D15): the on-prem package list, its
 * signed envelope, the internal pins, and what `GET /node/manifest` answers.
 *
 * A release is a set of **content-addressed packages** — `janitor`,
 * `state-service`, `production`, `web` — each a deterministic zip named
 * `<name>-<sha256>.zip` (Kelda's scheme). The **list** names them:
 *
 *     { schemaVersion: 1, commit, packages: [{ name, hash, url, version, dependencies, postInstall? }] }
 *
 * `url` is relative to the release store (`packages/<name>-<hash>.zip`); the
 * cloud answers each with a presigned GET URL. The list is published inside an
 * **envelope** that carries the list's exact text and a KMS signature over its
 * UTF-8 bytes (ECDSA P-256, SHA-256, DER; PLAN §11), so the node verifies the
 * very bytes that were signed — the envelope is one object and is replaced
 * atomically, never a list and a signature that can be read out of step.
 *
 * **Pins (D15)** are the vendor's support tool, never customer-facing: a
 * stage pin and a per-site pin name an immutable list by its hash. Precedence:
 * site → stage → latest. A running production's own pin is its
 * `packageHash` (§4.1), decided on the node, not here.
 *
 * The node parses what the cloud sends with these strict schemas: a newer list
 * format is a new `schemaVersion`, and a node that cannot read it keeps
 * running on what it has (offline-tolerant, §4.2).
 */
import { z } from 'zod';

/** A package's content hash: the SHA-256 of its zip, lowercase hex. */
export const sha256HexWireSchema = z.string().regex(/^[0-9a-f]{64}$/, 'a SHA-256 hash is 64 lowercase hex characters');

/** The packages a release is made of. The supervisor runs `janitor` and `state-service`; productions run `production`; the state service serves `web`. */
export const PACKAGE_NAMES = ['janitor', 'state-service', 'production', 'web'] as const;
export const packageNameWireSchema = z.enum(PACKAGE_NAMES);
export type PackageNameWire = z.infer<typeof packageNameWireSchema>;

/** Where a package lives in the release store, relative to its root. */
export function packageKey(name: string, hash: string): string {
  return `packages/${name}-${hash}.zip`;
}

export const packageEntryWireSchema = z
  .object({
    name: packageNameWireSchema,
    hash: sha256HexWireSchema,
    /** `packages/<name>-<hash>.zip`, relative to the release store. */
    url: z.string().regex(/^packages\/[a-z-]+-[0-9a-f]{64}\.zip$/),
    /** The commit it was built from. */
    version: z.string().min(1).max(64),
    /** Other packages of the same release it needs installed first (linked as `deps/<name>`). */
    dependencies: z.array(packageNameWireSchema).max(8),
    /** A command run in the unpacked directory before it is renamed into place (Kelda's `postInstall`). */
    postInstall: z.array(z.string().min(1)).min(1).max(32).optional(),
  })
  .strict()
  .refine((entry) => entry.url === packageKey(entry.name, entry.hash), { message: 'url must be packages/<name>-<hash>.zip' });
export type PackageEntryWire = z.infer<typeof packageEntryWireSchema>;

export const RELEASE_LIST_SCHEMA_VERSION = 1;

export const releaseListWireSchema = z
  .object({
    schemaVersion: z.literal(RELEASE_LIST_SCHEMA_VERSION),
    /** The commit the release was built from. */
    commit: z.string().min(1).max(64),
    packages: z.array(packageEntryWireSchema).min(1).max(32),
  })
  .strict()
  .refine((list) => new Set(list.packages.map((p) => p.name)).size === list.packages.length, { message: 'a package name appears twice' });
export type ReleaseListWire = z.infer<typeof releaseListWireSchema>;

/** The key a signature is made with (KMS key alias), and the one algorithm. */
export const SIGNING_ALGORITHM = 'ECDSA_SHA_256' as const;

/** A signed list, as stored (`packages.json`, `lists/<listHash>.json`). */
export const releaseEnvelopeWireSchema = z
  .object({
    /** The list, exactly as signed: parse it only after the signature verifies. */
    list: z.string().min(2).max(256 * 1024),
    /** Base64 DER ECDSA signature over the UTF-8 bytes of `list`. */
    signature: z.string().regex(/^[A-Za-z0-9+/]+=*$/).max(512),
    algorithm: z.literal(SIGNING_ALGORITHM),
    /** Which trusted key signed it (the node's trusted-key file names each by id). */
    keyId: z.string().min(1).max(256),
  })
  .strict();
export type ReleaseEnvelopeWire = z.infer<typeof releaseEnvelopeWireSchema>;

/** An internal pin (D15): which immutable list a stage or a site gets. */
export const releasePinWireSchema = z
  .object({
    /** SHA-256 of the pinned list's text: `lists/<listHash>.json`. */
    listHash: sha256HexWireSchema,
    pinnedAt: z.iso.datetime({ offset: true }),
    /** Why: a hold, a rollback, a support case. */
    note: z.string().max(512),
  })
  .strict();
export type ReleasePinWire = z.infer<typeof releasePinWireSchema>;

/** Which rule chose the list a node gets (`production-pin`: a cloud production's task, pinned to its production's release, PLAN §9.2). */
export const MANIFEST_SOURCES = ['site-pin', 'stage-pin', 'latest', 'production-pin'] as const;

/** `GET /node/manifest`: the effective signed list for the calling node, with a presigned URL per package. */
export const nodeManifestResponseWireSchema = z
  .object({
    envelope: releaseEnvelopeWireSchema,
    source: z.enum(MANIFEST_SOURCES),
    /** SHA-256 of `envelope.list`. */
    listHash: sha256HexWireSchema,
    /** Package `url` (as in the list) → a presigned GET URL, valid for `expiresInSeconds`. */
    urls: z.record(z.string(), z.url()),
    expiresInSeconds: z.number().int().positive(),
  })
  .strict();
export type NodeManifestResponseWire = z.infer<typeof nodeManifestResponseWireSchema>;
